import React, { useState, useEffect } from 'react';
import { Loader2, Table, Download, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { listDistantTables, syncRemoteTable, listRemoteTablesByServer } from '../../services/remoteTableService';
import { DistantTable, RemoteTable } from '../../types/remoteTable';

interface RemoteTablesBrowserProps {
  remoteServerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RemoteTablesBrowser: React.FC<RemoteTablesBrowserProps> = ({
  remoteServerId,
  open,
  onOpenChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [distantTables, setDistantTables] = useState<DistantTable[]>([]);
  const [syncedTables, setSyncedTables] = useState<RemoteTable[]>([]);
  const [syncingTable, setSyncingTable] = useState<string | null>(null);
  const { toast } = useToast();

  const loadTables = async () => {
    try {
      setLoading(true);
      const [distant, synced] = await Promise.all([
        listDistantTables(remoteServerId),
        listRemoteTablesByServer(remoteServerId),
      ]);
      setDistantTables(distant);
      setSyncedTables(synced);
    } catch (error: any) {
      console.error('Error loading tables:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load tables',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadTables();
    }
  }, [open, remoteServerId]);

  const handleSyncTable = async (tableName: string) => {
    setSyncingTable(tableName);
    try {
      await syncRemoteTable({
        remoteServerId,
        distantTableName: tableName,
      });
      toast({
        title: 'Table synced successfully',
        description: `${tableName} is now available for querying`,
      });
      loadTables();
    } catch (error: any) {
      console.error('Error syncing table:', error);
      toast({
        title: 'Failed to sync table',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSyncingTable(null);
    }
  };

  const isTableSynced = (tableName: string) => {
    return syncedTables.some((t) => t.distantTableName === tableName);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Browse Tables</DialogTitle>
          <DialogDescription>
            Select tables to sync and make available for querying
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && distantTables.length === 0 && (
          <div className="text-center py-12">
            <Table className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No tables available</p>
          </div>
        )}

        {!loading && distantTables.length > 0 && (
          <div className="space-y-2">
            {distantTables.map((table) => {
              const synced = isTableSynced(table.tableName);
              const syncing = syncingTable === table.tableName;

              return (
                <div
                  key={table.tableName}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Table className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{table.tableName}</span>
                      {synced && (
                        <Badge variant="secondary" className="ml-2">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Synced
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {table.columns.length} columns
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={synced ? 'outline' : 'default'}
                    onClick={() => handleSyncTable(table.tableName)}
                    disabled={synced || syncing}
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : synced ? (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Synced
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Sync Table
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && syncedTables.length > 0 && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              {syncedTables.length} table{syncedTables.length !== 1 ? 's' : ''} synced and ready
              to query
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RemoteTablesBrowser;
